// src/scene/controls.ts
import * as THREE from 'three';
import gsap from 'gsap';
import { t, pick } from '../i18n';
import type { WallHandle, WorkPlane } from './wall';
import type { Band } from './wallLayout';
import { applyInkHover, clearInkHover, COARSE } from './hoverFx';
import { HoverFrame, describable } from '../ui/hoverFrame';

export interface ControlsOpts {
  onSelect(plane: WorkPlane): void;
  frame: HoverFrame;
  onHoverWork?: (workId: string) => void;
}

export const STANDOFF = 4.6;        // default camera distance from the wall (x)
export const MIN_STANDOFF = 1.15;   // closest you can deep-zoom to the wall
const START_Y = 1.7;                // average human eye height (~178 cm person)
const LOOK = new THREE.Vector3(-1, 0, 0).normalize(); // gaze: straight at the tall wall
const PAN_SPEED = 0.011;            // world units per screen px
const INERTIA_DECAY = 0.92;
const EASE = 0.09;                  // position lerp
const TARGET_EASE = 0.12;           // look-target lerp
const CLICK_MAX_PX = 6;

export class Controls {
  enabled = true;

  // eased current vs target
  private pos = new THREE.Vector3();
  private posTarget = new THREE.Vector3();
  private look = new THREE.Vector3();
  private lookTarget = new THREE.Vector3();
  private vel = new THREE.Vector3();          // pan inertia (z, y)
  private diving = false;

  private dragging = false;
  private lastX = 0; private lastY = 0;
  private downX = 0; private downY = 0;
  private zMin: number; private zMax: number;
  private pinchDist = 0;

  private pointerNdc = new THREE.Vector2(2, 2);
  private raycaster = new THREE.Raycaster();
  private hovered: WorkPlane | null = null;
  private chip: HTMLDivElement;
  private onFirstDragCb: (() => void) | null = null;
  private firstDragFired = false;

  constructor(
    private camera: THREE.PerspectiveCamera,
    dom: HTMLElement,
    private wall: WallHandle,
    private opts: ControlsOpts,
  ) {
    this.chip = document.createElement('div');
    this.chip.className = 'title-chip';
    document.body.appendChild(this.chip);

    this.zMin = 0.5;                                          // across-wall (Z) bounds
    this.zMax = Math.max(this.zMin + 1, wall.width - 0.5);

    this.posTarget.set(STANDOFF, START_Y, wall.width / 2);    // open low + centred across
    this.pos.copy(this.posTarget);
    this.lookTarget.copy(this.pos).addScaledVector(LOOK, 6);
    this.look.copy(this.lookTarget);
    this.applyCamera();
    this.restPos.copy(this.posTarget);
    this.restLook.copy(this.lookTarget);

    dom.style.touchAction = 'none';
    dom.addEventListener('pointerdown', this.onDown);
    dom.addEventListener('wheel', this.onWheel, { passive: false });
    dom.addEventListener('touchstart', this.onTouchStart, { passive: false });
    dom.addEventListener('touchmove', this.onTouchMove, { passive: false });
    window.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerup', this.onUp);
    window.addEventListener('pointercancel', this.onUp);
  }

  setOnFirstDrag(fn: (() => void) | null): void {
    this.onFirstDragCb = fn;
    this.firstDragFired = false;
  }

  private applyCamera(): void {
    this.camera.position.copy(this.pos);
    this.camera.lookAt(this.look);
  }

  /** Lock the look-target a fixed distance straight ahead of the (clamped)
   *  position. Deriving it from posTarget — rather than accumulating pan deltas
   *  into it — means panning into the Y clamp can never drift the gaze off the
   *  camera (which previously pitched it away with no way back). */
  private syncLook(): void {
    this.lookTarget.copy(this.posTarget).addScaledVector(LOOK, 6);
  }

  /** Camera right/up in world space (for screen-aligned panning). */
  private basis(): { right: THREE.Vector3; up: THREE.Vector3 } {
    const fwd = this.look.clone().sub(this.pos).normalize();
    const right = new THREE.Vector3().crossVectors(fwd, this.camera.up);
    right.x = 0;                 // keep panning on the wall plane (no dolly into X)
    right.normalize();
    const up = new THREE.Vector3(0, 1, 0); // vertical pan is world-up
    return { right, up };
  }

  /** True when the wall's full Z-width fits in the horizontal frustum at the
   *  current standoff — then sideways pan is pointless and we keep the locked
   *  vertical climb (desktop). On narrow viewports (mobile portrait) or when
   *  zoomed in, the wall overflows the sides → sideways pan unlocks so the edge
   *  works become reachable. */
  private wallFullyVisible(): boolean {
    const vHalf = THREE.MathUtils.degToRad(this.camera.fov) / 2;
    const halfW = Math.tan(vHalf) * this.camera.aspect * this.posTarget.x; // wall at x=0
    return 2 * halfW >= this.wall.width;
  }

  private clampPos(): void {
    this.posTarget.x = THREE.MathUtils.clamp(this.posTarget.x, MIN_STANDOFF, STANDOFF + 1.5);
    // Climb stays within the art band: start at eye height (can't sink into the
    // floor below the first row) and stop before the ceiling void above the last.
    this.posTarget.y = THREE.MathUtils.clamp(
      this.posTarget.y, START_Y, Math.max(START_Y + 1, this.wall.height - 3.5));
    this.posTarget.z = THREE.MathUtils.clamp(this.posTarget.z, this.zMin, this.zMax);
  }

  private onDown = (e: PointerEvent) => {
    if (!this.enabled || this.diving) return;
    this.dragging = true;
    this.lastX = this.downX = e.clientX;
    this.lastY = this.downY = e.clientY;
    this.vel.set(0, 0, 0);
  };

  private onMove = (e: PointerEvent) => {
    this.pointerNdc.set((e.clientX / innerWidth) * 2 - 1, -(e.clientY / innerHeight) * 2 + 1);
    if (!this.dragging) return;
    if (!this.firstDragFired && (e.clientX !== this.lastX || e.clientY !== this.lastY)) {
      this.firstDragFired = true;
      this.onFirstDragCb?.();
    }
    const dx = e.clientX - this.lastX;
    const dy = e.clientY - this.lastY;
    this.lastX = e.clientX; this.lastY = e.clientY;
    // Tall wall: drag up → camera +Y, drag down → descend (the climb). Sideways
    // drag pans across Z, but only when the wall overflows the frustum — on wide
    // desktop the whole width is already in view so we keep the locked climb;
    // on mobile portrait (or zoomed in) the edges fall off-screen, so unlocking
    // Z-pan is the only way to reach the works hung at the far sides.
    const { right, up } = this.basis();
    const delta = up.multiplyScalar(-dy * PAN_SPEED);
    if (!this.wallFullyVisible()) delta.add(right.multiplyScalar(dx * PAN_SPEED));
    this.posTarget.add(delta);
    this.vel.copy(delta);
    this.clampPos();
    this.syncLook();   // keep the gaze straight & bounded, even against the Y clamp
  };

  private onUp = (e: PointerEvent) => {
    if (!this.dragging) return;
    this.dragging = false;
    const moved = Math.hypot(e.clientX - this.downX, e.clientY - this.downY);
    if (moved < CLICK_MAX_PX && this.enabled && !this.diving) {
      const plane = this.pick(e.clientX, e.clientY);
      if (plane) this.opts.onSelect(plane);
    }
  };

  private onWheel = (e: WheelEvent) => {
    if (!this.enabled || this.diving) return;
    e.preventDefault();
    this.zoomToward(e.deltaY);
  };

  /** Dolly toward/away from the wall point under the pointer. Restricted to the
   *  X (depth) and Y (height) plane — Z stays locked so the view never drifts
   *  sideways across the narrow wall — and the gaze stays level, so zoom feels
   *  predictable: scroll in/out, gently recentring on what you point at. */
  protected zoomToward(deltaY: number): void {
    const step = THREE.MathUtils.clamp(-deltaY * 0.0016, -0.2, 0.2); // + = zoom in
    const hit = this.wallHit();
    const targetX = 0;                                   // the wall plane
    const targetY = hit ? hit.y : this.posTarget.y;

    const nx = THREE.MathUtils.clamp(
      this.posTarget.x + (targetX - this.posTarget.x) * step, MIN_STANDOFF, STANDOFF + 1.5);
    const ddx = nx - this.posTarget.x;
    const ddy = (targetY - this.posTarget.y) * step;

    this.posTarget.x = nx;
    this.posTarget.y += ddy;
    this.lookTarget.x += ddx;   // keep the gaze offset constant in X → stays straight
    this.lookTarget.y += ddy;   // gaze tracks height → stays level
    this.vel.set(0, 0, 0);
    this.clampPos();
  }

  private touchMid(e: TouchEvent): void {
    const a = e.touches[0], b = e.touches[1];
    const mx = (a.clientX + b.clientX) / 2, my = (a.clientY + b.clientY) / 2;
    this.pointerNdc.set((mx / innerWidth) * 2 - 1, -(my / innerHeight) * 2 + 1);
  }
  private onTouchStart = (e: TouchEvent) => {
    if (e.touches.length === 2) {
      this.pinchDist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY);
    }
  };
  private onTouchMove = (e: TouchEvent) => {
    if (e.touches.length !== 2 || !this.enabled || this.diving) return;
    e.preventDefault();
    const d = Math.hypot(
      e.touches[0].clientX - e.touches[1].clientX,
      e.touches[0].clientY - e.touches[1].clientY);
    this.touchMid(e);
    this.zoomToward((this.pinchDist - d) * 6); // spread fingers → zoom in
    this.pinchDist = d;
  };

  private pickWorkPlane(hits: THREE.Intersection[]): WorkPlane | null {
    for (const hit of hits) {
      const plane = hit.object.userData.plane as WorkPlane | undefined;
      if (plane) return plane;
    }
    return null;
  }

  private pickNdc = new THREE.Vector2();
  private pick(clientX: number, clientY: number): WorkPlane | null {
    this.pickNdc.set((clientX / innerWidth) * 2 - 1, -(clientY / innerHeight) * 2 + 1);
    this.raycaster.setFromCamera(this.pickNdc, this.camera);
    return this.pickWorkPlane(this.raycaster.intersectObjects(this.wall.group.children, true));
  }

  /** Ray/plane (x=0) hit under the pointer — used for zoom-toward-cursor. */
  protected wallHit(): THREE.Vector3 | null {
    this.raycaster.setFromCamera(this.pointerNdc, this.camera);
    const plane = new THREE.Plane(new THREE.Vector3(1, 0, 0), 0);
    const out = new THREE.Vector3();
    return this.raycaster.ray.intersectPlane(plane, out) ? out : null;
  }

  /** Fly the camera to face a band's centre (its height up the wall). HUD jump-nav. */
  flyToSection(band: Band, duration = 1.2): void {
    const dest = new THREE.Vector3(STANDOFF, band.center.y, band.center.z);
    const destLook = dest.clone().addScaledVector(LOOK, 6);
    gsap.killTweensOf([this.posTarget, this.lookTarget]);
    this.vel.set(0, 0, 0);
    gsap.to(this.posTarget, { x: dest.x, y: dest.y, z: dest.z, duration, ease: 'power2.inOut' });
    gsap.to(this.lookTarget, { x: destLook.x, y: destLook.y, z: destLook.z, duration, ease: 'power2.inOut' });
  }

  private restPos = new THREE.Vector3();
  private restLook = new THREE.Vector3();

  /** Frame a work head-on for the detail dive. Stores the resting view for return. */
  diveTo(plane: WorkPlane, duration = 0.9): void {
    this.diving = true;
    this.enabled = false;
    this.restPos.copy(this.posTarget);
    this.restLook.copy(this.lookTarget);
    gsap.killTweensOf([this.posTarget, this.lookTarget]);
    this.vel.set(0, 0, 0);

    const halfH = plane.work.aspect; // frame distance ~ card height; tune in manual pass
    const dist = THREE.MathUtils.clamp(1.0 + halfH * 0.8, MIN_STANDOFF, STANDOFF);
    const dest = new THREE.Vector3(dist, plane.position.y, plane.position.z);
    gsap.to(this.posTarget, { x: dest.x, y: dest.y, z: dest.z, duration, ease: 'power2.inOut' });
    gsap.to(this.lookTarget, {
      x: plane.position.x, y: plane.position.y, z: plane.position.z,
      duration, ease: 'power2.inOut',
    });
  }

  /** Return to the resting view after the detail overlay closes. */
  resetZoom(): void {
    gsap.killTweensOf([this.posTarget, this.lookTarget]);
    const tl = gsap.timeline({
      onComplete: () => { this.diving = false; this.enabled = true; },
    });
    tl.to(this.posTarget, {
      x: this.restPos.x, y: this.restPos.y, z: this.restPos.z, duration: 0.8, ease: 'power2.out',
    }, 0);
    tl.to(this.lookTarget, {
      x: this.restLook.x, y: this.restLook.y, z: this.restLook.z, duration: 0.8, ease: 'power2.out',
    }, 0);
  }

  update(): void {
    if (!this.dragging && !this.diving) {
      this.posTarget.add(this.vel);
      this.vel.multiplyScalar(INERTIA_DECAY);
      this.clampPos();
      this.syncLook();
    }
    this.pos.lerp(this.posTarget, EASE);
    this.look.lerp(this.lookTarget, TARGET_EASE);
    this.applyCamera();
    this.updateHover();
  }

  /** True while the camera is still easing toward its target, coasting on
   *  inertia, dragging, or diving — i.e. the frame is visibly changing and the
   *  scene needs to keep rendering. Lets main.ts idle the render loop when the
   *  view has settled. */
  isMoving(): boolean {
    return (
      this.dragging ||
      this.diving ||
      this.vel.lengthSq() > 1e-7 ||
      this.pos.distanceToSquared(this.posTarget) > 1e-6 ||
      this.look.distanceToSquared(this.lookTarget) > 1e-6
    );
  }

  /** {x,y,z} of the eased camera — used by the e2e test API. */
  get cameraPos(): { x: number; y: number; z: number } {
    return { x: this.pos.x, y: this.pos.y, z: this.pos.z };
  }

  /** Eased world-Y the viewer is centred on — drives the P1 "follow-you"
   *  light pool. When hovering a work, bias toward its height so the pool
   *  leans into the piece the eye is on. Read-only; does not touch controls. */
  get focusY(): number {
    if (this.hovered) return this.pos.y * 0.7 + this.hovered.position.y * 0.3;
    return this.pos.y;
  }

  // ---- hover; dwell-focus removed (explicit click/scroll dive instead) ----
  private updateHover(): void {
    if (!this.enabled || this.dragging || this.diving) { this.setHovered(null); return; }
    this.raycaster.setFromCamera(this.pointerNdc, this.camera);
    const hits = this.raycaster.intersectObjects(this.wall.group.children, true);
    this.setHovered(this.pickWorkPlane(hits));
    if (this.hovered) {
      const pscreen = this.hovered.root.position.clone().project(this.camera);
      const x = (pscreen.x * 0.5 + 0.5) * innerWidth;
      const y = (-pscreen.y * 0.5 + 0.5) * innerHeight + 28;
      if (describable(this.hovered.work) && !COARSE) {
        this.opts.frame.teaser(this.hovered.work, x, y);
      } else {
        this.chip.style.transform = `translate(${x}px, ${y}px) translateX(-50%)`;
      }
    }
  }

  private setHovered(plane: WorkPlane | null): void {
    if (plane === this.hovered) return;
    if (this.hovered) {
      const h = this.hovered;
      clearInkHover(h.mesh);
      gsap.to(h.root.scale, { x: h.baseScale.x, y: h.baseScale.y, z: h.baseScale.z, duration: 0.35 });
      gsap.to(h.root.position, { x: h.position.x, duration: 0.35, ease: 'power2.out' });
    }
    this.hovered = plane;
    document.body.style.cursor = plane ? 'pointer' : '';
    if (!plane) { this.chip.classList.remove('visible'); this.opts.frame.hide(); return; }
    applyInkHover(plane.mesh, plane.work.palette[0]);
    this.opts.onHoverWork?.(plane.work.id);
    // Clean parallax pop: scale up + lift off the wall toward the viewer (+X).
    // No rotation — gsap lerps quaternion components linearly, which shears.
    const lift = plane.baseScale.x * 1.08;
    gsap.to(plane.root.scale, { x: lift, y: lift, z: lift, duration: 0.35, ease: 'power2.out' });
    gsap.to(plane.root.position, { x: plane.position.x + 0.14, duration: 0.35, ease: 'power2.out' });
    if (describable(plane.work) && !COARSE) {
      this.chip.classList.remove('visible');
      this.opts.frame.hide();
    } else {
      this.opts.frame.hide();
      this.chip.textContent = pick(plane.work.title) || t('untitled');
      this.chip.classList.add('visible');
    }
  }

  /** Test/debug hook used by window.__app.focus(). */
  focusForTest(plane: WorkPlane): void { this.setHovered(plane); }
}
